import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  DownloadReportComponent,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import { ReportingPermissions } from '@principle-theorem/principle-core/features';
import { ICustomReport } from '@principle-theorem/principle-core/interfaces';
import { ResolvedColumns } from '@principle-theorem/reporting';
import { filterUndefined, WithRef } from '@principle-theorem/shared';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReportBuilderCSV } from '../../../report-builder/report-builder-results-table/report-builder-csv';
import { ReportBuilderTableComponent } from '../../../report-builder/report-builder-results-table/report-builder-table/report-builder-table.component';
import { ReportBuilderStore } from '../../../report-builder/report-builder.store';
import { CustomReportsService } from '../../custom-reports.service';

@Component({
  selector: 'pr-custom-report-table',
  templateUrl: './custom-report-table.component.html',
  styleUrls: ['./custom-report-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DownloadReportComponent,
    MatButtonModule,
    MatIcon,
    MatTooltip,
    NgCommonModule,
    ReportBuilderTableComponent,
  ],
})
export class CustomReportTableComponent {
  private _organisation = inject(OrganisationService);
  store = inject(ReportBuilderStore);
  customReportsService = inject(CustomReportsService);
  editCustomReportPermissions = [
    ReportingPermissions.ReportingCustomReportEdit,
  ];
  csvData$: Observable<object[]>;
  csvTranslator$: Observable<ReportBuilderCSV>;

  customReport = input.required<WithRef<ICustomReport>>();
  readonly = input(false);
  hasEditPermissions$: Observable<boolean>;

  constructor() {
    this.hasEditPermissions$ = this._organisation.userHasPermission$(
      ReportingPermissions.ReportingCustomReportEdit
    );
    this.csvData$ = this.store.results$.pipe(
      map((results) => results?.filtered ?? [])
    );
    this.csvTranslator$ = combineLatest([
      this.store.dataSource$.pipe(filterUndefined()),
      this.store.display$,
      this.store.currentReport$.pipe(map((report) => report?.name)),
    ]).pipe(
      map(
        ([dataSource, display, reportName]) =>
          new ReportBuilderCSV(
            ResolvedColumns.toColumns(dataSource, display?.columns ?? []),
            reportName
          )
      )
    );
  }

  async editColumns(customReport: WithRef<ICustomReport>): Promise<void> {
    const updatedColumns =
      await this.customReportsService.editTableColumns(customReport);
    if (!updatedColumns) {
      return;
    }
    await this.store.reloadReport();
  }
}
